import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Card, Chip, IconButton, List, ListItem, ListItemText, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AppIcon from '../common/AppIcon';
import PriorityChip from '../common/PriorityChip';
import { Task } from '../../services/tasks';
import { Topic } from '../../services/topics';
import { Planner } from '../../services/planners';
import { formatDate } from '../../utils/formatDate';

interface UpcomingTasksProps {
  tasks: Task[];
  planners?: Planner[];
  topics?: Topic[];
  onComplete?: (task: Task, e: React.MouseEvent) => void;
  onView?: (task: Task) => void;
}

const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
const priorityColors: Record<string, string> = { high: '#C26A6A', medium: '#D4A84E', low: '#B5ADA5' };

const sortTasks = (tasks: Task[]) =>
  [...tasks].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    if (a.due_date && b.due_date) {
      const dateCmp = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      if (dateCmp !== 0) return dateCmp;
    } else if (a.due_date && !b.due_date) return -1;
    else if (!a.due_date && b.due_date) return 1;
    return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
  });

const isTaskOverdue = (task: Task) => {
  if (task.status === 'completed' || !task.due_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(task.due_date) < today;
};

const TaskRow: React.FC<{
  task: Task;
  planner: Planner | null;
  topicEmoji?: string;
  isMobile?: boolean;
  onClick: () => void;
  onComplete?: (e: React.MouseEvent) => void;
}> = ({ task, planner, topicEmoji, isMobile, onClick, onComplete }) => {
  const overdue = isTaskOverdue(task);
  return (
  <ListItem
    onClick={onClick}
    sx={{
      bgcolor: 'grey.50',
      borderRadius: 2,
      mb: 1,
      border: '1px solid',
      borderColor: 'grey.200',
      borderLeft: '3px solid',
      borderLeftColor: priorityColors[task.priority] || 'grey.200',
      cursor: 'pointer',
      overflow: 'hidden',
      position: 'relative',
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: 'grey.100',
        transform: 'scale(1.01)',
        boxShadow: '0 2px 8px rgba(176,122,143,0.08)',
      },
      '@media (hover: hover)': {
        '&:hover .complete-btn': { opacity: 1, left: 0 },
        '&:hover .task-content': { transform: 'translateX(36px)' },
      },
      ...(isMobile && { flexWrap: 'wrap' }),
      gap: 1,
    }}
  >
    {onComplete && (
      <Tooltip title="Mark as Done">
        <IconButton
          size="small"
          className="complete-btn"
          onClick={(e) => { e.stopPropagation(); onComplete(e); }}
          sx={{
            color: 'success.main',
            opacity: 0,
            position: 'absolute',
            left: -36,
            transition: 'opacity 0.2s, left 0.2s, transform 0.15s, background-color 0.15s, box-shadow 0.15s',
            zIndex: 1,
            ml: 1,
            borderRadius: '50%',
            '&:hover': {
              transform: 'scale(1.3)',
              bgcolor: 'rgba(107,158,120,0.12)',
              boxShadow: '0 0 8px rgba(107,158,120,0.35)',
            },
          }}
        >
          <AppIcon name="check-circle" fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    {isMobile ? (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {task.is_pinned && <span style={{ fontSize: 14 }}>📌</span>}
          {topicEmoji && <span>{topicEmoji}</span>}
          <Typography variant="body1" sx={{ fontWeight: 500 }}>{task.title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="body2" sx={{ color: overdue ? 'error.main' : 'text.secondary', fontWeight: overdue ? 600 : 400 }}>
            {task.due_date ? formatDate(task.due_date) : 'No due date'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <PriorityChip priority={task.priority} />
            {planner && (
              <Chip
                label={planner.name}
                size="small"
                variant="outlined"
                avatar={
                  <Avatar sx={{ bgcolor: planner.color || 'grey.400', width: 20, height: 20 }}>
                    <AppIcon name="person" sx={{ fontSize: 14, color: 'white' }} />
                  </Avatar>
                }
                sx={{
                  color: planner.color || 'grey.800',
                  borderColor: planner.color || '#E4DED8',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        </Box>
      </>
    ) : (
      <>
        <ListItemText
          className="task-content"
          sx={{ flex: 1, m: 0, transition: 'transform 0.2s' }}
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {task.is_pinned && <span style={{ fontSize: 14 }}>📌</span>}
              {topicEmoji && <span>{topicEmoji}</span>}
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{task.title}</Typography>
            </Box>
          }
          secondary={
            task.due_date
              ? <Typography component="span" variant="body2" sx={{ color: overdue ? 'error.main' : 'text.secondary', fontWeight: overdue ? 600 : 400 }}>
                  {overdue ? 'Overdue: ' : 'Due: '}{formatDate(task.due_date)}
                </Typography>
              : 'No due date'
          }
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          <PriorityChip priority={task.priority} />
          {planner && (
            <Chip
              label={planner.name}
              size="small"
              variant="outlined"
              avatar={
                <Avatar sx={{ bgcolor: planner.color || 'grey.400', width: 20, height: 20 }}>
                  <AppIcon name="person" sx={{ fontSize: 14, color: 'white' }} />
                </Avatar>
              }
              sx={{
                color: planner.color || 'grey.800',
                borderColor: planner.color || '#E4DED8',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      </>
    )}
  </ListItem>
  );
};

const SectionHeader: React.FC<{ title: string; count: number }> = ({ title, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
    <Typography variant="h6" sx={{ fontSize: '1.15rem' }}>
      {title}
    </Typography>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        bgcolor: 'primary.light',
        color: 'primary.dark',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {count}
    </Box>
    <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, #E4DED8 0%, transparent 100%)' }} />
  </Box>
);

const UpcomingTasks: React.FC<UpcomingTasksProps> = ({ tasks, planners = [], topics = [], onComplete, onView }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const plannerMap = Object.fromEntries(planners.map((p) => [p.id, p]));
  const topicMap = Object.fromEntries(topics.map((t) => [t.id, t]));

  const inProgress = sortTasks(tasks.filter((t) => t.status === 'in_progress'));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);

  const inProgressIds = new Set(inProgress.map((t) => t.id));
  const dueThisWeek = sortTasks(tasks.filter((t) => {
    if (t.status === 'completed' || !t.due_date || inProgressIds.has(t.id)) return false;
    const due = new Date(t.due_date + 'T00:00:00');
    return due < nextWeek;
  }));

  const dueThisWeekIds = new Set(dueThisWeek.map((t) => t.id));
  const assigned = sortTasks(tasks.filter((t) =>
    t.status === 'pending' && t.assigned_planner_id && !inProgressIds.has(t.id) && !dueThisWeekIds.has(t.id)
  ));

  const getTopicEmoji = (task: Task) => {
    if (!task.topic_id) return undefined;
    const topic = topicMap[task.topic_id];
    return topic?.emoji || undefined;
  };

  const handleClick = (task: Task) => {
    if (onView) {
      onView(task);
    } else {
      navigate(`/tasks?view=${task.id}`);
    }
  };

  const hasNone = inProgress.length === 0 && dueThisWeek.length === 0 && assigned.length === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {hasNone && (
        <Card>
          <Typography variant="body2" color="text.secondary">
            No active tasks.
          </Typography>
        </Card>
      )}

      {inProgress.length > 0 && (
        <Card>
          <SectionHeader title="In Progress" count={inProgress.length} />
          <List disablePadding>
            {inProgress.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                planner={task.assigned_planner_id ? plannerMap[task.assigned_planner_id] : null}
                topicEmoji={getTopicEmoji(task)}
                isMobile={isMobile}
                onClick={() => handleClick(task)}
                onComplete={!isMobile && onComplete ? (e) => onComplete(task, e) : undefined}
              />
            ))}
          </List>
        </Card>
      )}

      {dueThisWeek.length > 0 && (
        <Card>
          <SectionHeader title="Due This Week" count={dueThisWeek.length} />
          <List disablePadding>
            {dueThisWeek.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                planner={task.assigned_planner_id ? plannerMap[task.assigned_planner_id] : null}
                topicEmoji={getTopicEmoji(task)}
                isMobile={isMobile}
                onClick={() => handleClick(task)}
                onComplete={!isMobile && onComplete ? (e) => onComplete(task, e) : undefined}
              />
            ))}
          </List>
        </Card>
      )}

      {assigned.length > 0 && (
        <Card>
          <SectionHeader title="Assigned" count={assigned.length} />
          <List disablePadding>
            {assigned.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                planner={task.assigned_planner_id ? plannerMap[task.assigned_planner_id] : null}
                topicEmoji={getTopicEmoji(task)}
                isMobile={isMobile}
                onClick={() => handleClick(task)}
                onComplete={!isMobile && onComplete ? (e) => onComplete(task, e) : undefined}
              />
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
};

export default UpcomingTasks;
